/**
 * formDeriveTargetKey
 *
 * The written location of one derived value, as a JSON tuple. ONE spelling for
 * three judgments that must never drift: the engine's duplicate-target dedupe in
 * `createFormDeriveEngineConfig`, the authoring validator's `duplicateTarget`
 * problem in `validateFormDeriveDraft`, and the drawer's "already derived by"
 * disabled target option in `FormDeriveEntryPanel`. A drawer that accepted a
 * target the engine then dropped would compute nothing and not even bump the rev.
 * A JSON tuple rather than a delimiter join, so a free-form id cannot forge a
 * collision. Two overloads because a validated `FormDeriveEntry['target']` always
 * names a location, while a draft target may name neither — `undefined` is then
 * the honest answer, never an empty-string key two drafts would share.
 *
 * A row projection owns a whole table, so its key names the table and nothing
 * else: one table, one owning derive, and a second entry targeting it collides
 * here rather than being discovered when a run produces two row sets. A target
 * carrying a `typeId` alongside its `tableId` is the retired col shape and keys
 * as `undefined`, never as a table.
 */

import { isRecordValue } from './isRecordValue';
import type { FormDeriveEntry } from '../System.FormDerive.types';

function formDeriveTargetKey(target: FormDeriveEntry['target']): string;
function formDeriveTargetKey(target: unknown): string | undefined;
function formDeriveTargetKey(target: unknown): string | undefined {
  if (!isRecordValue(target)) return undefined;
  if (typeof target.fieldId === 'string' && target.fieldId !== '') {
    return JSON.stringify(['field', target.fieldId]);
  }
  if (typeof target.tableId === 'string' && target.tableId !== '' && target.typeId === undefined) {
    return JSON.stringify(['table', target.tableId]);
  }
  return undefined;
}

export { formDeriveTargetKey };
